import { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { LuChevronLeft, LuChevronRight, LuPlay, LuX } from 'react-icons/lu';

import EpisodeThumbnail from './EpisodeThumbnail';
import ResourcesPodcastPlayer from './ResourcesPodcastPlayer';
import SbrIcon from './SbrIcon';
import type { PodcastShow } from '../../data/podcastShows';
import { getPodcastShowPlaylistUrl } from '../../data/podcastShows';
import { fetchYouTubeEpisodesForShow, type YouTubeEpisode } from '../../lib/youtube';

type Props = {
  show: PodcastShow;
  onClose: () => void;
};

export default function PodcastShowEpisodesSection({ show, onClose }: Props) {
  const sectionRef = useRef<HTMLElement>(null);
  const episodesTrackRef = useRef<HTMLDivElement>(null);

  const [episodes, setEpisodes] = useState<YouTubeEpisode[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeEpisode, setActiveEpisode] = useState<YouTubeEpisode | null>(null);
  const [shouldAutoplay, setShouldAutoplay] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  useEffect(() => {
    sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [show.id]);

  useEffect(() => {
    let cancelled = false;

    async function loadShowEpisodes() {
      setLoading(true);
      try {
        const latest = await fetchYouTubeEpisodesForShow(show.id, 12);
        if (!cancelled) {
          setEpisodes(latest);
          setActiveEpisode(latest[0] ?? null);
          setShouldAutoplay(false);
        }
      } catch {
        if (!cancelled) {
          setEpisodes([]);
          setActiveEpisode(null);
          toast.error(`Could not load episodes for ${show.title}.`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadShowEpisodes();
    return () => {
      cancelled = true;
    };
  }, [show.id, show.title]);

  function selectEpisode(ep: YouTubeEpisode) {
    setShouldAutoplay(true);
    setActiveEpisode(ep);
  }

  function updateEpisodeCarouselScroll() {
    const track = episodesTrackRef.current;
    if (!track) return;
    const { scrollLeft, scrollWidth, clientWidth } = track;
    const edge = 4;
    setCanScrollLeft(scrollLeft > edge);
    setCanScrollRight(scrollLeft + clientWidth < scrollWidth - edge);
  }

  function scrollEpisodesBy(direction: 'prev' | 'next') {
    const track = episodesTrackRef.current;
    if (!track) return;
    const card = track.querySelector<HTMLElement>('.resources-episode-card');
    const step = card ? card.offsetWidth + 20 : 340;
    track.scrollBy({ left: direction === 'next' ? step : -step, behavior: 'smooth' });
  }

  useEffect(() => {
    const track = episodesTrackRef.current;
    if (!track || episodes.length === 0) return;

    updateEpisodeCarouselScroll();
    track.addEventListener('scroll', updateEpisodeCarouselScroll, { passive: true });
    const observer = new ResizeObserver(updateEpisodeCarouselScroll);
    observer.observe(track);

    return () => {
      track.removeEventListener('scroll', updateEpisodeCarouselScroll);
      observer.disconnect();
    };
  }, [episodes]);

  return (
    <section
      ref={sectionRef}
      className="podcast-show-episodes"
      aria-labelledby={`podcast-show-episodes-${show.id}`}
    >
      <div className="podcast-show-episodes__header">
        <div className="podcast-show-episodes__intro">
          <img
            src={show.image}
            alt=""
            className="podcast-show-episodes__cover"
            width={3000}
            height={3000}
            decoding="async"
          />
          <div>
            <h2 id={`podcast-show-episodes-${show.id}`} className="podcast-show-episodes__title fw-bold mb-1">
              {show.title}
            </h2>
            <p className="podcast-show-episodes__category mb-0">{show.category}</p>
          </div>
        </div>
        <button type="button" className="podcast-show-episodes__close" onClick={onClose}>
          <SbrIcon icon={LuX} size={18} />
          <span>Close</span>
        </button>
      </div>

      {loading ? (
        <p className="podcast-show-episodes__status text-secondary mb-0">Loading episodes from YouTube…</p>
      ) : episodes.length === 0 ? (
        <p className="podcast-show-episodes__status text-secondary mb-0">
          No episodes found for this show on YouTube right now.
        </p>
      ) : (
        <>
          {activeEpisode ? (
            <article className="resources-podcast-featured podcast-show-episodes__featured">
              <div className="resources-podcast-featured__content">
                <span className="resources-podcast-featured__badge">
                  {activeEpisode.id === episodes[0]?.id ? 'Latest Episode' : 'Now Playing'}
                </span>
                <h3 className="resources-podcast-featured__heading">{activeEpisode.title}</h3>
                <p className="resources-podcast-featured__guest">
                  with{' '}
                  <span className="resources-podcast-featured__guest-name">{activeEpisode.guestName}</span> /{' '}
                  {activeEpisode.guestTitle}
                </p>
                <a
                  className="resources-podcast-featured__watch-link"
                  href={activeEpisode.watchUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Open on YouTube
                </a>
              </div>
              <div className="resources-podcast-featured__player-wrap">
                <ResourcesPodcastPlayer
                  videoId={activeEpisode.id}
                  title={activeEpisode.title}
                  thumbnailUrl={activeEpisode.thumbnailUrl}
                  autoplay={shouldAutoplay}
                />
              </div>
            </article>
          ) : null}

          <div className="resources-podcast__carousel podcast-show-episodes__carousel">
            <button
              type="button"
              className={[
                'resources-podcast__carousel-btn',
                'resources-podcast__carousel-btn--prev',
                !canScrollLeft && 'resources-podcast__carousel-btn--hidden',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => scrollEpisodesBy('prev')}
              disabled={!canScrollLeft}
              aria-label="Scroll to previous episodes"
            >
              <SbrIcon icon={LuChevronLeft} size={22} />
            </button>
            <div ref={episodesTrackRef} className="resources-podcast__episodes">
              {episodes.map((ep) => {
                const isActive = activeEpisode?.id === ep.id;
                return (
                  <article
                    key={ep.id}
                    className={['resources-episode-card', isActive && 'resources-episode-card--active']
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <button
                      type="button"
                      className="resources-episode-card__select"
                      onClick={() => selectEpisode(ep)}
                      aria-label={`Play ${ep.title}`}
                      aria-pressed={isActive}
                    >
                      <div className="resources-episode-card__thumb">
                        <EpisodeThumbnail videoId={ep.id} />
                        <span className="resources-episode-card__play-overlay" aria-hidden>
                          <SbrIcon icon={LuPlay} size={20} />
                        </span>
                      </div>
                      <h4 className="resources-episode-card__title">{ep.title}</h4>
                    </button>
                  </article>
                );
              })}
            </div>
            <button
              type="button"
              className={[
                'resources-podcast__carousel-btn',
                'resources-podcast__carousel-btn--next',
                !canScrollRight && 'resources-podcast__carousel-btn--hidden',
              ]
                .filter(Boolean)
                .join(' ')}
              onClick={() => scrollEpisodesBy('next')}
              disabled={!canScrollRight}
              aria-label="Scroll to next episodes"
            >
              <SbrIcon icon={LuChevronRight} size={22} />
            </button>
          </div>

          <p className="resources-podcast__view-all-wrap mb-0">
            <a
              className="resources-podcast__view-all"
              href={getPodcastShowPlaylistUrl(show)}
              target="_blank"
              rel="noreferrer"
            >
              <span>View All on YouTube</span>
              <SbrIcon icon={LuChevronRight} size={18} className="resources-podcast__view-all-icon" />
            </a>
          </p>
        </>
      )}
    </section>
  );
}
